// AudioView - View details of an audio file

import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { audioInfo } from '../store/audioSlice';
import { initWaveSurfer, destroyWaveSurfer } from '../utils/waveUtils';

import { formatDateAsFriendlyDate, formatListAsString, formatDuration } from '../utils/formatUtils';
import { useAuthCheckAndNavigate } from '../utils/authUtils';

// Import the config object from the config.js file
import config from '../config/config';
// pull variables from the config object
const audioBaseURL = config.adminServer.audioBaseURL;
const coverImageURLBase = config.app.coverImageURLBase;
// const fieldNotes = config.audio.fieldNotes;

function AudioView() {
  const { audioID } = useParams();
  const dispatch = useDispatch();

  // get auth state from Redux store
  useAuthCheckAndNavigate('audioView');

  const { user: userAuth } = useSelector((state) => state.auth);
  const [editPerm, setEditPerm] = useState(false);
  const [listPerm, setListPerm] = useState(false);
  const [specialTagsPerm, setSpecialTagsPerm] = useState(false);

  // Check if the user has permission to edit/list audio
  useEffect(() => {
    if (!userAuth?.permissions) return;
    if (userAuth.permissions.indexOf('audioEdit') !== -1) setEditPerm(true);
    if (userAuth.permissions.indexOf('audioList') !== -1) setListPerm(true);
    if (userAuth.permissions.indexOf('specialTags') !== -1) setSpecialTagsPerm(true);
  }, [userAuth.permissions]);

  const [isDomReady, setIsDomReady] = useState(false);
  const waveSurferRef = useRef(null);

  // Success and error handling
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [audioError, setAudioError] = useState('');
  const [coverImgError, setCoverImgError] = useState(false);

  const [record, setRecord] = useState({
    copyrightCert: 0,
  });

  useEffect(() => {
    if (!audioID) return;
    setIsLoading(true); // Start loading

    dispatch(audioInfo({audioID}))
      .unwrap()
      .then(response => {
        setRecord({
          ...response,
          classification: formatListAsString(response.classification),
          tags: formatListAsString(response.tags),
          internalTags: formatListAsString(response.internalTags),
          createDate: formatDateAsFriendlyDate(response.createDate),
          editDate: formatDateAsFriendlyDate(response.editDate),
        });
        setIsLoading(false); // Stop loading once data is fetched
      })
      .catch(err => {
        console.error('Error fetching audio details:', err);
        setError(err || 'Failed to fetch audio details.');
        setIsLoading(false); // Stop loading on error
      });
  }, [audioID, dispatch]);

  // This useEffect ensures the component is mounted before initializing WaveSurfer
  useEffect(() => {
    setIsDomReady(true);
    // console.log('Component mounted');
  }, []);

  // initialize WaveSurfer once the component is mounted and audioDetails.filename is available
  useEffect(() => {  //
    if (isDomReady && record.filename) {
      const audioURL = `${audioBaseURL}/${record.filename}`;
      // check if waveSurferRef.current is already initialized
      if (waveSurferRef.current) {
        destroyWaveSurfer();
      }
      // Initialize a new WaveSurfer instance
      initWaveSurfer(audioURL, (wavesurfer) => {
        // console.log('WaveSurfer is ready:', wavesurfer);
      }).then(wavesurfer => {
        waveSurferRef.current = wavesurfer;
      }).catch(err => {
        setAudioError('Audio file not available.');
      });
    }
    // Cleanup function to destroy WaveSurfer instance on component unmount
    return () => {  //
      if (waveSurferRef.current) {
        destroyWaveSurfer();
      }
    };
  }, [isDomReady, record.filename]);

  const renderBreadcrumbs = () => {
    return (
      <div className="breadcrumb-box">
        <ul className="breadcrumb">
          {listPerm && (
            <li className="link"><Link to="/audio/list">List</Link></li>
          )}
          <li className="link"><Link to="/audio/upload">Add New</Link></li>
          {editPerm && (
            <li className="link"><Link to={'/audio/edit/' + audioID}>Edit</Link></li>
          )}
        </ul>
      </div>
    );
  };  

  return ( 
    <div className="edit-wrapper">
      <div className="display-box-wrapper">
        <div className="display-box">
          <h2 className='title'>View Audio Details</h2>
          {renderBreadcrumbs()}
          <div className="form-group">
            <div className="form-group-with-image">
              <div className="form-fields">
                <div className="form-row">
                  <span className="form-label">Title:</span>
                  <span className="form-value">{record.title}</span>
                </div>

                <div className="form-row">
                  <span className="form-label">Filename:</span>
                  <span className="form-value">{record.filename}</span>
                </div>

                <div className="form-row">
                  <span className="form-label">Created:</span>
                  <span className="form-value">
                    <Link to={`/profile/${record.creatorUsername}`}>
                      {record.creatorUsername}
                    </Link>
                    {" on " + record.createDate}
                  </span>
                </div>

                {record.editorUsername && (
                  <div className="form-row">
                    <span className="form-label">Edited:</span>
                    <span className="form-value">
                      <Link to={`/profile/${record.editorUsername}`}>
                        {record.editorUsername}
                      </Link>
                      {" on " + record.editDate}
                    </span>
                  </div>
                )}

                <div className="form-row">
                  <span className="form-label">Status:</span>
                  <span className="form-value">{record.status}</span>
                </div>

                <div className="form-row">
                  <span className="form-label">Plays:</span>
                  <span className="form-value">{record.timesUsed || '—'}</span>
                </div>
              </div>

              <div className="cover-image-panel">
                {record.coverImage && !coverImgError ? (
                  <img
                    className="cover-image"
                    src={`${coverImageURLBase}/${record.coverImage}.jpg`}
                    alt="Cover"
                    onError={() => setCoverImgError(true)}
                  />
                ) : (
                  <div className="cover-image-placeholder">{coverImgError ? 'Image not found' : 'No cover image'}</div>
                )}
              </div>
            </div>
          </div>

          <div className="form-group pb-2">
            {audioError
              ? <p className="media-unavailable">{audioError}</p>
              : <div id="waveform"></div>
            }
            <div className="text-sm mt-1">Duration: {formatDuration(record.duration)}</div>
          </div>
  
          <div className="form-group">
            <div className="form-col">
              <div className="form-label">Classification:</div>
              <div className="form-value">
                {record.classification}
              </div>
            </div>
            <div className="form-col">
              <div className="form-label">Tags:</div>
              <div className="form-value">{record.tags}</div>
            </div>
            {specialTagsPerm && (
              <div className="form-col">
                <div className="form-label">Internal Tags:</div>
                <div className="form-value">{record.internalTags || '—'}</div>
              </div>
            )}
            <div className="form-col">
              <div className="form-label">Comments:</div>
              <div className="form-value multiline">{record.comments}</div>
            </div>
          </div>

          <div className='message-box'>
            {isLoading && <p className="success">Loading...</p>}
            {error && <p className="error">{error}</p>}
          </div>
  
          {renderBreadcrumbs()}
        </div>
      </div>
    </div>
  );  
}

export default AudioView;
